import { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import { GOLD, GOLD2, GOLDF } from '../theme/theme';
import TopSlideshow, { SLIDES } from './TopSlideshow';
import LocationRow from './LocationRow';

/**
 * Hero — the full poster layout (brochure + slideshow + feature cards +
 * layout map), rebuilt as: static brochure panel (left), a LIVE
 * crossfading slideshow (top-right, 4 real photo-pairs, working
 * arrows + dots), and the static feature-cards/layout-map artwork
 * (bottom-right). Everything is real static poster artwork except the
 * slideshow, which genuinely transitions.
 */
export default function HeroSlider() {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setCurrent(c => (c + 1) % SLIDES.length), 4500);
    return () => clearInterval(id);
  }, [current]);

  const prev = () => setCurrent(c => (c - 1 + SLIDES.length) % SLIDES.length);
  const next = () => setCurrent(c => (c + 1) % SLIDES.length);

  return (
    <Box sx={{
      position: 'relative',
      width: '100%',
      minHeight: { xs: 'auto', md: 'calc(100vh - 113px)' },
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
      pt: { xs: 0.75, md: 1 },
      pb: { xs: 1, md: 1.5 },
      background: `
        radial-gradient(ellipse 80% 60% at 15% 20%, rgba(220,225,235,0.55) 0%, transparent 55%),
        radial-gradient(ellipse 70% 55% at 85% 15%, rgba(200,210,225,0.45) 0%, transparent 55%),
        radial-gradient(ellipse 65% 70% at 25% 85%, rgba(180,190,205,0.5) 0%, transparent 55%),
        radial-gradient(ellipse 75% 65% at 80% 80%, rgba(210,218,230,0.4) 0%, transparent 55%),
        linear-gradient(135deg, #b8bdc7 0%, #d8dce3 25%, #a9aeb8 50%, #e2e5ea 75%, #9ba0ab 100%)
      `,
      backgroundSize: '200% 200%, 200% 200%, 200% 200%, 200% 200%, 300% 300%',
      animation: 'silverSwirl 14s ease-in-out infinite',
      '@keyframes silverSwirl': {
        '0%':   { backgroundPosition: '0% 50%, 100% 0%, 0% 100%, 100% 100%, 0% 0%' },
        '25%':  { backgroundPosition: '30% 20%, 70% 30%, 20% 70%, 80% 70%, 40% 30%' },
        '50%':  { backgroundPosition: '100% 50%, 0% 100%, 100% 0%, 0% 0%, 100% 100%' },
        '75%':  { backgroundPosition: '70% 80%, 30% 70%, 80% 30%, 20% 30%, 60% 70%' },
        '100%': { backgroundPosition: '0% 50%, 100% 0%, 0% 100%, 100% 100%, 0% 0%' },
      },
      '&::before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        background: 'radial-gradient(ellipse 50% 40% at 50% 40%, rgba(255,255,255,0.35) 0%, transparent 70%)',
        mixBlendMode: 'screen',
        animation: 'silverGlow 6s ease-in-out infinite alternate',
        pointerEvents: 'none',
      },
      '@keyframes silverGlow': {
        '0%':   { opacity: 0.4, transform: 'scale(1)' },
        '100%': { opacity: 0.8, transform: 'scale(1.15)' },
      },
    }}>

      {/* ── Poster: two separate boxes, side by side, with a gap between ── */}
      <Box sx={{
        position: 'relative',
        zIndex: 2,
        width: { xs: '98%', md: '97%', lg: '96%' },
        maxWidth: 1680,
        opacity: 0,
        transform: 'scale(0.97)',
        animation: 'posterIn 0.9s cubic-bezier(0.22,1,0.36,1) forwards',
        display: 'flex',
        alignItems: 'flex-start',
        flexDirection: { xs: 'column', md: 'row' },
        gap: { xs: 2, md: 2.5 },
        '@keyframes posterIn': {
          to: { opacity: 1, transform: 'scale(1)' },
        },
      }}>

        {/* Box 1: brochure panel — repeating zoom in/out */}
        <Box sx={{
          position: 'relative',
          width: { xs: '100%', md: '27%' },
          aspectRatio: '500 / 802',
          overflow: 'hidden',
          flexShrink: 0,
          borderRadius: '18px',
          boxShadow: `0 20px 64px rgba(0,0,0,0.28), 0 0 60px rgba(201,150,12,0.14)`,
        }}>
          <Box
            component="img"
            src="/hero-brochure.jpg"
            alt="Elaris Luxury Enclave brochure — plot sizes, pricing, features and contact details"
            sx={{
              position: 'absolute', inset: 0,
              width: '100%', height: '100%',
              objectFit: 'cover',
              display: 'block',
              animation: 'brochureZoom 7s ease-in-out infinite',
              '@keyframes brochureZoom': {
                '0%, 100%': { transform: 'scale(1)' },
                '50%':      { transform: 'scale(1.09)' },
              },
            }}
          />
        </Box>

        {/* Box 2: live slideshow + static feature cards / layout map */}
        <Box sx={{
          width: { xs: '100%', md: '73%' },
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}>
          <TopSlideshow current={current} onPrev={prev} onNext={next} />
          <Box
            component="img"
            src="/hero-features-map.jpg"
            alt="Elaris Luxury Enclave features — DTCP approved, black top roads, gated community, avenue plantation, and scenic surroundings"
            sx={{ width: '100%', height: 'auto', display: 'block' }}
          />
          <LocationRow />
        </Box>
      </Box>

      {/* ── Dot indicators — control the live slideshow ── */}
      <Box sx={{ position: 'relative', zIndex: 2, display: 'flex', gap: 0.9, mt: 2.2 }}>
        {SLIDES.map((_, i) => (
          <Box
            key={i}
            onClick={() => setCurrent(i)}
            sx={{
              width: i === current ? 24 : 8,
              height: 8,
              borderRadius: '4px',
              background: i === current ? `linear-gradient(90deg, ${GOLD}, ${GOLD2})` : 'rgba(11,13,30,0.20)',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: i === current ? `0 0 8px ${GOLD}88` : 'none',
            }}
          />
        ))}
      </Box>
    </Box>
  );
}
